import { Column, Entity, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { TeamUserEntity } from './team-user.entity';

/**
 * What a member's coverage role grants over the clients their team covers.
 *
 * `none` is the DEFAULT and preserves today's behaviour exactly: being on a
 * team grants no client access whatsoever. That is the single most important
 * property of this feature — tenants group people into teams for lists and
 * reporting, and that grouping must not quietly become an access grant.
 */
export const TEAM_ROLE_COVERAGE_VALUES = ['none', 'view', 'edit'] as const;

export type TeamRoleCoverageModel = (typeof TEAM_ROLE_COVERAGE_VALUES)[number];

/**
 * A named role somebody can hold on a team — "Division Lead", "Regional VP",
 * "Sales Rep" (PD-16309). Per tenant, and the tenant chooses both the name and
 * what it grants.
 *
 * Coverage on the ROLE rather than on the team is a deliberate step past the
 * reference product: Salesforce sets the access level per territory, so
 * everybody in it gets the same, which cannot express a view-only VP sitting
 * beside a manager who can edit.
 *
 * Holding a coverage role means seeing the clients of this team AND of every
 * team nested beneath it. The roster itself grants nothing — see
 * `docs/claude/record-visibility/HIERARCHY-AND-TEAMS.md` §4.
 */
@Entity('team_role')
export class TeamRoleEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 100 })
	public name!: string;

	@Column({
		name: 'coverageAccess',
		type: 'enum',
		enum: TEAM_ROLE_COVERAGE_VALUES,
		default: "'none'",
	})
	public coverageAccess!: TeamRoleCoverageModel;

	/** Display order in the role picker. Lowest first. */
	@Column('int', { name: 'sortOrder', default: 0 })
	public sortOrder!: number;

	/**
	 * Retired roles stay on the memberships that hold them — revoking access by
	 * deleting a role would be a silent, invisible change — but drop out of the
	 * pickers.
	 */
	@Column({ type: 'boolean', name: 'isActive', width: 1, default: 1 })
	public isActive!: boolean;

	@OneToMany(() => TeamUserEntity, (teamUser) => teamUser.fkTeamRole, {})
	public teamUsers!: Array<TeamUserEntity>;
}
